import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { AlertTriangle, Edit, ListTodo } from "lucide-react";

const STATUS_META = {
  idea: { label: "Idea", header: "text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800", border: "border-slate-200 dark:border-slate-700" },
  backlog: { label: "Backlog", header: "text-purple-700 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/40", border: "border-purple-200 dark:border-purple-800" },
  in_progress: { label: "En Progreso", header: "text-sky-700 dark:text-sky-400 bg-sky-100 dark:bg-sky-900/40", border: "border-sky-200 dark:border-sky-800" },
  pilot: { label: "Piloto", header: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40", border: "border-amber-200 dark:border-amber-800" },
  scaling: { label: "Escalando", header: "text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/40", border: "border-orange-200 dark:border-orange-800" },
  completed: { label: "Completado", header: "text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/40", border: "border-green-200 dark:border-green-800" },
  cancelled: { label: "Cancelado", header: "text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/40", border: "border-red-200 dark:border-red-800" },
};

// Fix 2026-10-25 (ticket P-4): 'evaluation'/'validation' se retiran del catálogo
// (acordado con el usuario). Las iniciativas que los tuvieran se migran a
// Backlog vía POST /cross-link/migrate-legacy-statuses.
const COLUMN_ORDER = ["idea", "backlog", "in_progress", "pilot", "scaling", "completed", "cancelled"];

const typeLabels = { digital: "Digital", open_innovation: "Open Innovation", rd: "I+D", pilot: "Piloto" };

export function PortfolioKanban({ initiatives, onStatusChange, onOpen, onQuickEdit, isDesynced, taskCounts }) {
  const getByStatus = (status) => initiatives.filter((i) => i.status === status);

  // Fix 2026-10-25 (ticket R-1): cualquier iniciativa con un status que ya no
  // está en el catálogo (ej. 'validation' en producción, pendiente de migrar)
  // antes desaparecía del Kanban sin avisar (194 vs 196 en la lista). Ahora se
  // agrupa en una columna "Sin estado: X" por cada valor desconocido — y se
  // puede arrastrar directamente a una columna válida para resolverlo a mano.
  const unknownStatuses = [...new Set(initiatives.map((i) => i.status).filter((s) => s && !STATUS_META[s]))];
  const columns = [...COLUMN_ORDER, ...unknownStatuses];

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    await onStatusChange(draggableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((status) => {
          const meta = STATUS_META[status] || {
            label: `Sin estado: "${status}"`,
            header: "text-rose-700 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/40",
            border: "border-rose-300 dark:border-rose-800",
          };
          const colInits = getByStatus(status);
          return (
            <div key={status} className="min-w-[250px] w-[250px] flex-shrink-0 flex flex-col">
              <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-x border-t ${meta.border} ${meta.header}`}>
                <span className="font-semibold text-sm">{meta.label}</span>
                <span className="text-xs font-mono opacity-70">{colInits.length}</span>
              </div>
              <Droppable droppableId={status}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 min-h-[180px] max-h-[70vh] overflow-y-auto p-2 space-y-2 rounded-b-lg border ${meta.border} transition-opacity ${snapshot.isDraggingOver ? "bg-primary/5" : ""}`}
                    data-testid={`kanban-column-${status}`}
                  >
                    {colInits.map((init, index) => (
                      <Draggable key={init.id} draggableId={init.id} index={index}>
                        {(dragProvided, dragSnapshot) => (
                          <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                            <Card
                              className={`select-none ${dragSnapshot.isDragging ? "shadow-xl rotate-1 opacity-95" : "cursor-grab active:cursor-grabbing hover:shadow-md"}`}
                              onClick={() => onOpen(init.id)}
                              data-testid={`kanban-initiative-${init.id}`}
                            >
                              <CardContent className="p-3 space-y-1.5">
                                <div className="flex items-start justify-between gap-1">
                                  <div className="flex items-center gap-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{init.name}</p>
                                    {isDesynced?.(init) && <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                                  </div>
                                  <button
                                    type="button"
                                    className="p-0.5 hover:bg-muted rounded flex-shrink-0"
                                    onClick={(e) => { e.stopPropagation(); onQuickEdit(e, init); }}
                                    data-testid={`kanban-quick-edit-${init.id}`}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </button>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-2">{init.description}</p>
                                <div className="flex items-center gap-2 flex-wrap pt-0.5">
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">{typeLabels[init.type] || init.type}</Badge>
                                  {init.sponsor && <span className="text-[10px] text-muted-foreground">· {init.sponsor}</span>}
                                  {(() => {
                                    const open = Object.entries(taskCounts?.[init.id] || {}).filter(([s]) => s !== "done" && s !== "cancelled").reduce((a, [, n]) => a + n, 0);
                                    const blocked = (taskCounts?.[init.id] || {}).blocked || 0;
                                    if (!open) return null;
                                    return (
                                      <span className={`flex items-center gap-0.5 text-[10px] ${blocked ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`} data-testid={`kanban-task-count-${init.id}`}>
                                        <ListTodo className="w-3 h-3" />{open}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
